import { Link } from "wouter-preact";
import { routes } from "../lib/route";
import { useLayoutEffect, useRef, useState } from "preact/hooks";
import { selectPortalIdentity } from "../features/auth/authSelectors";
import { useAppSelector } from "../store/hooks";
import { getApiFailureMessage } from "../lib/gboApiClient";
import { ErrorPanel, LoadingPanel } from "../components/StatePanels";
import {
  linkedAccountsView,
  type AccountLinkResult,
} from "../features/linkedAccounts/linkedAccountsView";
import { useGetPortalAccountsQuery, type PortalAccount } from "../store/portalAuthApi";

import { AddEmailFlow } from "../features/linkedAccounts/AddEmailFlow";
import { EmailIcon } from "../components/icons";
import "../features/linkedAccounts/linkedAccounts.css";

export function LinkedAccountsPage() {
  const identity = useAppSelector(selectPortalIdentity);
  const accountsQuery = useGetPortalAccountsQuery(identity?.loginId);
  if (!accountsQuery.currentData) {
    if (accountsQuery.isError)
      return (
        <ErrorPanel
          title="Unable to load linked accounts"
          message={getApiFailureMessage(accountsQuery.error, "Please try again.")}
          onRetry={() => void accountsQuery.refetch()}
        />
      );
    return <LoadingPanel title="Loading linked accounts" />;
  }

  return (
    <LinkedAccountsContent
      verifiedEmails={identity?.verifiedEmails ?? []}
      accounts={accountsQuery.currentData}
      reloadAccounts={() => accountsQuery.refetch().unwrap()}
    />
  );
}

export function LinkedAccountsContent({
  verifiedEmails,
  accounts,
  reloadAccounts,
}: {
  verifiedEmails: string[];
  accounts: PortalAccount[];
  reloadAccounts: () => Promise<PortalAccount[]>;
}) {
  const [adding, setAdding] = useState(false);
  const [result, setResult] = useState<AccountLinkResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const addEmailButton = useRef<HTMLButtonElement>(null);
  const resultElement = useRef<HTMLDivElement>(null);
  const focusAfterClose = useRef(false);
  const pending = useRef(false);
  const linkedAccountIds = useRef<string[]>([]);
  const view = linkedAccountsView.create(verifiedEmails, accounts, result);

  useLayoutEffect(() => {
    if (!adding && focusAfterClose.current) {
      (resultElement.current ?? addEmailButton.current)?.focus();
      focusAfterClose.current = false;
    }
  }, [adding]);

  async function run(action: () => Promise<void>) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (failure) {
      setError(getApiFailureMessage(failure, "Unable to complete this request. Please try again."));
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  async function refreshResult(previousIds: string[]) {
    const accounts = await reloadAccounts();
    setResult({ previousIds, accounts });
  }

  function cancel() {
    focusAfterClose.current = true;
    setAdding(false);
    setError(null);
  }

  function begin() {
    setResult(null);
    setError(null);
    linkedAccountIds.current = accounts.map((account) => account.customerId);
    setAdding(true);
  }

  function validateEmail(address: string) {
    return verifiedEmails.some((known) => known.toLowerCase() === address.toLowerCase())
      ? "This email is already verified for your sign-in."
      : undefined;
  }

  function completeLink() {
    const previousIds = linkedAccountIds.current;
    setResult({ previousIds, accounts: null });
    cancel();
    void run(() => refreshResult(previousIds));
  }

  function retryAccounts() {
    if (result) void run(() => refreshResult(result.previousIds));
  }

  return (
    <section class="linked-accounts" aria-labelledby="linked-accounts-title">
      <header class="linked-accounts-heading">
        <Link href={`~${routes.portal.overview()}`} className="link-action">
          <span aria-hidden="true">← </span>Back to overview
        </Link>
        <h2 id="linked-accounts-title">Linked accounts</h2>
      </header>
      <div class="linked-email-groups">
        {view.emailGroups.map(({ address, accounts }) => (
          <section class="linked-email-group" key={address} aria-label={address}>
            <header class="linked-email-heading">
              <span class="linked-account-email-icon">
                <EmailIcon />
              </span>
              <h3>{address}</h3>
            </header>
            <ul class="linked-account-list">
              {accounts.map((account) => (
                <li key={account.customerId}>
                  <div>
                    <strong>{account.name}</strong>
                    <span>
                      {account.code} · {account.serviceAddress}
                    </span>
                  </div>
                </li>
              ))}
              {accounts.length === 0 && <li class="muted">No linked accounts</li>}
            </ul>
          </section>
        ))}
      </div>
      {!adding && (
        <div>
          <button
            class="primary-action"
            disabled={busy || Boolean(result && !result.accounts)}
            ref={addEmailButton}
            onClick={begin}
          >
            Add another email
          </button>
        </div>
      )}
      {(adding || result || error) && (
        <section
          class="linked-accounts-card linked-accounts-card--raised"
          aria-label="Add another email"
        >
          {result && (
            <div class="payment-success" role="status" ref={resultElement} tabIndex={-1}>
              <strong>Email verified.</strong>
              {view.newAccounts ? (
                <>
                  {view.newAccounts.length ? (
                    <>
                      <p>You now have access to:</p>
                      <ul>
                        {view.newAccounts.map((account) => (
                          <li key={account.customerId}>
                            {account.name} · {account.code}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p>No additional accounts were found.</p>
                  )}
                  <p>Your account selection hasn't changed.</p>
                </>
              ) : (
                <p>
                  {busy
                    ? "Refreshing your accounts…"
                    : "Your email was verified, but we couldn't refresh your accounts."}
                </p>
              )}
            </div>
          )}
          {error && (
            <p class="payment-error" role="alert">
              {error}
            </p>
          )}
          {result && !result.accounts ? (
            <button class="secondary-action" disabled={busy} onClick={() => void retryAccounts()}>
              Retry loading accounts
            </button>
          ) : adding ? (
            <AddEmailFlow
              validateEmail={validateEmail}
              onComplete={completeLink}
              onCancel={cancel}
            />
          ) : null}
        </section>
      )}
    </section>
  );
}
